import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../components/ui/sonner';
import { Plus, Trash2, Shield, User, Edit, Search } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [role, setRole] = useState('user');
  const [submitting, setSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { t, isRtl } = useLanguage();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t.accessDenied);
        navigate('/dashboard');
      } else if (error.response?.status === 401) {
        localStorage.removeItem('token');
        toast.error(t.sessionExpired);
        navigate('/login');
      } else {
        toast.error(t.somethingWentWrong);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!username || !password) {
      toast.error(isRtl ? 'اسم المستخدم وكلمة المرور مطلوبان' : 'Username and password are required');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/users`,
        { username, password, role, name: name || null, employee_id: employeeId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t.userCreated);
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.somethingWentWrong);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setName(user.name || '');
    setEmployeeId(user.employee_id || '');
    setRole(user.role);
    setNewPassword('');
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: name || null,
        employee_id: employeeId || null,
        role,
      };
      
      if (newPassword) {
        updateData.password = newPassword;
      }

      await axios.put(
        `${BACKEND_URL}/api/users/${editingUser.username}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t.userUpdated);
      setEditDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.somethingWentWrong);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    if (!window.confirm(t.deleteUserConfirm)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/users/${userToDelete.username}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t.userDeleted);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.somethingWentWrong);
    }
  };

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setName('');
    setEmployeeId('');
    setRole('user');
    setEditingUser(null);
    setNewPassword('');
  };

  return (
    <Layout>
      <div data-testid="users-page" className="space-y-6">
        <div className={`flex flex-col md:flex-row md:justify-between md:items-center gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className={isRtl ? 'text-right' : ''}>
            <h1 className="text-4xl font-bold text-slate-900">{t.usersTitle}</h1>
            <p className="text-slate-600 mt-2">{t.usersSubtitle}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-user-button"
                className={`bg-slate-900 hover:bg-slate-800 text-white ${isRtl ? 'flex-row-reverse' : ''}`}
              >
                <Plus className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                {t.createUser}
              </Button>
            </DialogTrigger>
            <DialogContent className="border-slate-200 bg-white">
              <DialogHeader className="border-b border-blue-200 pb-4">
                <DialogTitle className={`text-2xl font-bold text-blue-900 ${isRtl ? 'text-right' : ''}`}>{t.createNewUser}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className={isRtl ? 'block text-right' : ''}>{t.username}</Label>
                  <Input
                    data-testid="new-username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t.enterUsername}
                    className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                    dir={isRtl ? 'rtl' : 'ltr'}
                  />
                </div>
                <div>
                  <Label className={isRtl ? 'block text-right' : ''}>{t.password}</Label>
                  <Input
                    data-testid="new-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.enterPasswordMin}
                    className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                    dir={isRtl ? 'rtl' : 'ltr'}
                  />
                </div>
                <div>
                  <Label className={isRtl ? 'block text-right' : ''}>{t.name}</Label>
                  <Input
                    data-testid="new-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.enterName}
                    className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                    dir={isRtl ? 'rtl' : 'ltr'}
                  />
                </div>
                <div>
                  <Label className={isRtl ? 'block text-right' : ''}>{t.employeeId}</Label>
                  <Input
                    data-testid="new-employee-id-input"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    placeholder={t.enterEmployeeId}
                    className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                    dir={isRtl ? 'rtl' : 'ltr'}
                  />
                </div>
                <div>
                  <Label className={isRtl ? 'block text-right' : ''}>{t.role}</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className={`mt-2 ${isRtl ? 'text-right' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">{t.user}</SelectItem>
                      <SelectItem value="admin">{t.admin}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <Button
                    data-testid="submit-create-user"
                    onClick={handleCreateUser}
                    disabled={submitting}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    {submitting ? t.creating : t.createUser}
                  </Button>
                  <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="flex-1">
                    {t.cancel}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative">
          <Search className={`absolute ${isRtl ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-blue-500 w-5 h-5`} />
          <Input
            data-testid="search-users-input"
            type="text"
            placeholder={t.searchUsers}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${isRtl ? 'pr-10 text-right' : 'pl-10'} h-11 border-2 border-blue-300 focus:border-blue-500 focus:ring-blue-500 placeholder:text-slate-400`}
            dir={isRtl ? 'rtl' : 'ltr'}
          />
        </div>

        {loading ? (
          <div className="text-center py-12">{t.loading}</div>
        ) : filteredUsers.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-slate-500">{t.noUsers}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((user) => (
              <Card
                key={user.username}
                data-testid={`user-card-${user.username}`}
                className="p-6 bg-white border border-slate-200"
              >
                <div className={`flex items-start justify-between mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <div className={`p-2 rounded-full ${user.role === 'admin' ? 'bg-blue-100' : 'bg-slate-100'}`}>
                      {user.role === 'admin' ? (
                        <Shield className="w-5 h-5 text-blue-600" />
                      ) : (
                        <User className="w-5 h-5 text-slate-600" />
                      )}
                    </div>
                    <div className={isRtl ? 'text-right' : ''}>
                      <p className="font-semibold text-slate-900">{user.username}</p>
                      {user.name && <p className="text-sm text-slate-600">{user.name}</p>}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    user.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                  }`}>
                    {user.role === 'admin' ? t.admin : t.user}
                  </span>
                </div>

                {user.employee_id && (
                  <p className={`text-sm text-slate-500 mb-4 ${isRtl ? 'text-right' : ''}`}>
                    {isRtl ? `${user.employee_id} :ID` : `ID: ${user.employee_id}`}
                  </p>
                )}

                <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditUser(user)}
                    className={`flex-1 text-blue-600 hover:bg-blue-50 ${isRtl ? 'flex-row-reverse' : ''}`}
                  >
                    <Edit className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} />
                    {t.edit}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteUser(user)}
                    className={`flex-1 text-red-600 hover:bg-red-50 ${isRtl ? 'flex-row-reverse' : ''}`}
                  >
                    <Trash2 className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} />
                    {t.delete}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="border-slate-200 bg-white">
            <DialogHeader className="border-b border-blue-200 pb-4">
              <DialogTitle className={`text-2xl font-bold text-blue-900 ${isRtl ? 'text-right' : ''}`}>{t.editUser}: {editingUser?.username}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className={isRtl ? 'block text-right' : ''}>{t.name}</Label>
                <Input
                  data-testid="edit-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.enterName}
                  className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
              </div>
              <div>
                <Label className={isRtl ? 'block text-right' : ''}>{t.employeeId}</Label>
                <Input
                  data-testid="edit-employee-id-input"
                  type="text"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  placeholder={t.enterEmployeeId}
                  className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
              </div>
              <div>
                <Label className={isRtl ? 'block text-right' : ''}>{t.role}</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className={`mt-2 ${isRtl ? 'text-right' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">{t.user}</SelectItem>
                    <SelectItem value="admin">{t.admin}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={isRtl ? 'block text-right' : ''}>{t.newPassword}</Label>
                <Input
                  data-testid="edit-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••"
                  className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
              </div>
              <div className={`flex gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <Button
                  data-testid="submit-edit-user"
                  onClick={handleUpdateUser}
                  disabled={submitting}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                >
                  {submitting ? t.updating : t.update}
                </Button>
                <Button variant="outline" onClick={() => { setEditDialogOpen(false); resetForm(); }} className="flex-1">
                  {t.cancel}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Users;
